import React, {Component} from "react";
import * as ActionplanData from "./ActionplanData";
import {categories, categoryLabels} from "./Controls";
import {Ticket} from "Stores/dashboardStore/tickets";
import * as _ from "lodash";
import * as d3 from "d3";
import TipTapEditor from "Components/Common/TheEditor";
import { Utils } from "../../../common/utils";
import { Link } from "react-router-dom";
import { draftToProseMirrorJSON, safeJsonParse } from "@/lib/convertDraftToProseMirror";


function makeGrouped(actionplanData: ActionplanData.ActionplanState) {
    return new ActionplanData.GroupedActionplans(
        actionplanData.filteredActionplans,
        [
            ActionplanData.categorize,
            ActionplanData.viewByAccessors[actionplanData.viewBy]
        ]
    );
}

function calculateDays(older: Date, newer: Date) {
    const diffMs = newer.getTime() - older.getTime();
    // There are 86400000 ms in a day
    return Math.floor(diffMs / (86400000));
}

interface TimelineCommentsProps {
    actionplan: Ticket
}

interface TimelineCommentsState {
    visible: boolean
}

export class TimelineComments extends Component<TimelineCommentsProps, TimelineCommentsState> {

    ref!: HTMLDivElement | null;

    constructor(props: TimelineCommentsProps) {
        super(props);
        this.state = {
            visible: false
        };
    }

    componentDidMount() {
        document.addEventListener("click", this.handleOutsideFocus.bind(this));
        document.addEventListener("keydown", this.handleEscape.bind(this));
    }

    componentWillUnmount() {
        document.removeEventListener("click", this.handleOutsideFocus);
        document.removeEventListener("keydown", this.handleEscape);
    }

    close() {
        this.setState({ visible: false });
    }

    handleOutsideFocus(event) {
        if (this.ref && !this.ref.contains(event.target)) {
            this.close();
        }
    }

    handleEscape(event) {
        if (event.key === "Escape") {
            this.close();
        }
    }

    toggleVisibility(e) {
        e.stopPropagation();
        this.setState({ visible: !this.state.visible });
    }

    render() {
        if (this.props.actionplan.comments?.length > 0) {
            return (
                <div ref={ref => this.ref = ref}>
                    <div className={`comments-view ${this.state.visible ? "visible" : ""}`}>
                        <div className={"row"}>
                            <div className={"col-auto"}>
                                <strong>ID: </strong>
                                {
                                    this.props.actionplan.ui_url ?
                                        <a href={this.props.actionplan.ui_url}>{this.props.actionplan.display_id}</a>
                                        : this.props.actionplan.display_id
                                }
                            </div>
                            <div className={"col close"}>
                                <i className={"icon icon-close"} onClick={this.close.bind(this)} />
                            </div>
                        </div>
                        <p>
                            <strong>Assignee:</strong> { this.props.actionplan.assignee_name }
                        </p>
                        <p>
                            <strong>Due Date:</strong> {
                                this.props.actionplan.evaluation_date ?
                                    Utils.parseISOLocalDate(this.props.actionplan.evaluation_date as string).toLocaleDateString()
                                    : null
                            }
                        </p>
                        <div className={"comment-content"} key={`tl-comment-content-${this.props.actionplan.ruuid}`}>
                            <p>
                                <strong>Insight:</strong> { this.props.actionplan.dashboard_interpretation }
                            </p>
                            <p><strong>Comments:</strong></p>
                            {this.props.actionplan.comments.map((comment) => {
                                const parsedComment = safeJsonParse(comment.comment); 
                                const comment_text = parsedComment && parsedComment.type === "doc" 
                                    ? parsedComment
                                    : parsedComment && parsedComment.blocks
                                        ? draftToProseMirrorJSON(parsedComment as any)
                                        : parsedComment;
                                
                                return (
                                    <div className={"comment"} key={_.uniqueId("tl-comment-")}>
                                        <p>
                                            <strong>By </strong>{comment.created_by}
                                            <strong> on </strong>{new Date(comment.created as string).toLocaleDateString() }
                                        </p>
                                        <div><TipTapEditor id={`comment-apdb-${comment.id}`} value={comment_text} editable={false} showToolbar={false} /></div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <span className={"link"} onClick={this.toggleVisibility.bind(this)}>{this.props.actionplan.comments.length}</span>
                </div>
            );
        }
        return (<span>0</span>);
    }
}

interface TimelineAxisProps {
    minDate?: Date
    maxDate?: Date
}

class TimelineAxis extends Component<TimelineAxisProps> {

    svgRef: SVGElement | null;
    viewWidth = 300;
    viewHeight = 25;
    sideMargin = 5;


    handleResize () {
        this.draw();
    }

    componentDidMount() {
        this.handleResize = this.handleResize.bind(this);
        this.draw();
        window.addEventListener("resize", this.handleResize);
    }

    componentDidUpdate() {
        this.draw();
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    draw() {
        const svg = d3.select(this.svgRef)
            .attr("width", "100%")
            .attr("height", this.viewHeight);

        svg.selectAll("*").remove();

        const svgSize = svg.node()?.getBoundingClientRect();

        const x = d3.scaleTime()
            .domain([d3.utcDay(this.props.minDate), d3.utcDay(this.props.maxDate)])
            .range([this.sideMargin, (svgSize?.width || this.viewWidth) - this.sideMargin]);

        const xAxis = d3.axisTop(x)
            .tickFormat(d3.timeFormat("%b %e, %y"))
            .ticks(5)
            .tickSize(3);

        svg.append("g")
            .attr("transform", `translate(0, ${this.viewHeight})`)
            .call(xAxis);
    }

    render() {
        return (
            <svg className={"axis"} ref={ref => this.svgRef = ref}/>
        );
    }
}

// We may make the d3 scale a prop passed from the parent for speed.
interface TimelineBarProps {
    actionplan: Ticket
    minDate?: Date
    maxDate?: Date
    category: string
}

export class TimelineBar extends Component<TimelineBarProps> {
    svgRef: SVGElement | null;
    toolTipRef: Element | null;

    viewWidth = 300;
    viewHeight = 20;
    sideMargin = 5;
    actualHeight = 7.5;
    intendedHeight = 15;
    todayMargin = 2.5;
    commentWidth = 1.5;
    commentHeight = 10;

    today = new Date();
    startDate: Date; // Where the bar starts
    actualEndDate: Date; // Where the 'actual' bar ends
    expectedCloseDate: Date;

    constructor(props: TimelineBarProps) {
        super(props);

        this.startDate = new Date(this.props.actionplan.created);
        // Set the end date if the actionplan is closed. Otherwise, we want the bar to look like it is continueing.
        this.actualEndDate = this.props.actionplan.status === "closed" ? new Date(this.props.actionplan.updated) : this.today;

        this.expectedCloseDate = Utils.parseISOLocalDate(this.props.actionplan.evaluation_date as string);
    }

    componentDidMount() {
        this.draw();
    }

    componentDidUpdate() {
        this.draw();
    }

    draw() {
        const svg = d3.select(this.svgRef);
        svg.selectAll("*").remove();
        svg.attr("viewBox", [0, 0, this.viewWidth, this.viewHeight])
            .attr("preserveAspectRatio", "none");

        const x = d3.scaleTime()
            .domain([d3.utcDay(this.props.minDate), d3.utcDay(this.props.maxDate)])
            .range([this.sideMargin, this.viewWidth - this.sideMargin]);

        const today = svg.append("g")
            .attr("class", "today");

        today.append("line")
            .attr("x1", x(this.today))
            .attr("y1", this.todayMargin)
            .attr("x2", x(this.today))
            .attr("y2", this.viewHeight / 2 - this.todayMargin);

        today.append("line")
            .attr("x1", x(this.today))
            .attr("y1", this.viewHeight / 2 + this.todayMargin)
            .attr("x2", x(this.today))
            .attr("y2", this.viewHeight - this.todayMargin);


        svg.append("g")
            .attr("class", "intended")
            .append("rect")
            .attr("x", x(this.startDate))
            .attr("y", this.viewHeight / 2 - this.intendedHeight / 2)
            .attr("width", Math.max(
                1,
                x(this.expectedCloseDate) - x(this.startDate)
            ))
            .attr("height", this.intendedHeight);


        svg.append("g")
            .attr("class", "actual")
            .append("rect")
            .attr("class", this.props.category)
            .attr("x", x(this.startDate))
            .attr("y", this.viewHeight / 2 - this.actualHeight / 2)
            .attr("width", Math.max(
                1, // Have at least a little bit of bar
                x(this.actualEndDate) - x(this.startDate)
            ))
            .attr("height", this.actualHeight)
            .on("mouseover", (event) => {
                d3.select(this.toolTipRef)
                    .style("display", "block")
                    .style("left", (event.pageX + 50) + "px")
                    .style("top", event.pageY + "px");
            })
            .on("mousemove", (event) => {
                d3.select(this.toolTipRef)
                   .style("left", (event.pageX + 50) + "px")
                   .style("top", event.pageY + "px");
            })
            .on("mouseleave", () => {
                d3.select(this.toolTipRef).style("display", "none");
            });

        // Not all actionplans will have comments.
        if (this.props.actionplan.comments && this.props.actionplan.comments.length > 0) {
            svg.append("g")
                .selectAll()
                .data(this.props.actionplan.comments)
                .join("rect")
                .attr("class", "comment")
                .attr("x", c => x(new Date(c.created)) - this.commentWidth / 2)
                .attr("width", this.commentWidth)
                .attr("y", this.viewHeight / 2 - this.commentHeight / 2)
                .attr("height", this.commentHeight);
        }
    }

    render() {
        return (
            <div>
                <div className={"datapoint"} ref={ref => this.toolTipRef = ref}>
                    <p>Status: <strong>{ categoryLabels[this.props.category] }</strong></p>
                    <p>
                        Date Created: <strong>{ new Date(this.props.actionplan.created).toLocaleDateString() }</strong><br/>
                        Due Date: <strong>{
                                      this.props.actionplan.evaluation_date ?
                                          Utils.parseISOLocalDate(this.props.actionplan.evaluation_date as string).toLocaleDateString()
                                          : null
                                  }</strong>
                    </p>
                    <p>
                        Days planned to be open: <strong>{ Math.max(calculateDays(this.startDate, this.expectedCloseDate), 1)}</strong><br/>
                        Days Overdue: <strong>{ Math.max(calculateDays(this.expectedCloseDate, this.props.actionplan.status === "closed" ? this.actualEndDate : this.today), 0)}</strong>
                    </p>
                    <p>
                        Tied to strategic objective?: <strong>{ this.props.actionplan.strategic_plan_item_id ? "Yes" : "No"}</strong><br/>
                        Display Id:<strong>{ this.props.actionplan.display_id }</strong>
                    </p>
                </div>
                <svg width="100%" height="30px" className={"bar"} ref={ref => this.svgRef = ref} />
            </div>
        );
    }
}


interface ActionplanTimelineProps {
    actionplanData: ActionplanData.ActionplanState
    onFocus?: (actionplan: Ticket | undefined) => void
}

interface ActionplanTimelineState {
    grouped: ActionplanData.GroupedActionplans
    focusedActionplan?: Ticket
}

export class ActionplanTimeline extends Component<ActionplanTimelineProps, ActionplanTimelineState> {


    constructor(props: ActionplanTimelineProps) {
        super(props);
        this.state = {
            grouped: makeGrouped(this.props.actionplanData),
            focusedActionplan: undefined
        };
    }

    static getDerivedStateFromProps(props: ActionplanTimelineProps) {
        const nextState: Partial<ActionplanTimelineState> = {
            grouped: makeGrouped(props.actionplanData)
        };
        return nextState;
    }

    includeCategory(category: string) {
        // Don't render if there are no actionplans in this category.
        if (this.props.actionplanData.counts[category] === 0) {
            return false;
        }
        // Don't render if this category isn't included.
        if (category === categories.OPEN_LATE) {
            return this.props.actionplanData.includeAllStatus || this.props.actionplanData.includeOpenLate;
        } else if (category === categories.OPEN_ON_TIME) {
            return this.props.actionplanData.includeAllStatus || this.props.actionplanData.includeOpenOnTime;
        } else if (category === categories.CLOSED_MET) {
            return this.props.actionplanData.includeAllStatus || this.props.actionplanData.includeClosedMet;
        } else if (category === categories.CLOSED_NOT_MET) {
            return this.props.actionplanData.includeAllStatus || this.props.actionplanData.includeClosedNotMet;
        }
        return false;
    }

    toggleFocus(actionplan: Ticket) {
        /**
         * If the same actionplan is focused again, it is unfocused.
         * If an actionplan is focused for the first time, it is focused.
         */
        const next: Ticket | undefined = (actionplan.ruuid === this.state.focusedActionplan?.ruuid) ? undefined : actionplan;
        this.setState({
            focusedActionplan: next
        }, () => {
            if (this.props.onFocus) {
                this.props.onFocus(next);
            }
        });
    }

    renderCategory(category: string) {
        const rows: Array<JSX.Element> = [];
        const categoryGroup = this.state.grouped.grouped[category] as ActionplanData.GroupedActionplans;
        let printedCategory = false;
        for (const viewBy of (categoryGroup).sorted()) {
            let printedView = false;
            for (const actionplan of categoryGroup.grouped[viewBy] as Ticket[]) {
                // TODO - We don't have a way of viewing comments yet.
                // It seems like comments are added when you go to a specific ticket.
                rows.push(
                    <tr className={this.state.focusedActionplan?.ruuid === actionplan.ruuid ? "focused" : undefined} key={_.uniqueId("timeline-")} onClick={() => {this.toggleFocus(actionplan);} }>
                        {!printedCategory ? <td rowSpan={this.state.grouped.lengths[category]} className={"data status"}>{categoryLabels[category]}</td> : null}
                        {!printedView ? <td rowSpan={categoryGroup.lengths[viewBy]} className={"data selected-view"}>{ viewBy }</td> : null }
                        <td className={"data display-id"}>{
                            actionplan!.dashboard_id === null ? actionplan.display_id :
                                <Link to={{ pathname: Utils.getActionplanUrl(actionplan.dashboard, actionplan.ruuid) }}>{ actionplan.display_id }</Link>
                        }</td>
                        <td className={"data comment-count"}><TimelineComments actionplan={actionplan}/></td>
                        <td className={"timeline"}>
                            <TimelineBar
                                actionplan={actionplan}
                                minDate={this.props.actionplanData.minFilteredDate}
                                maxDate={this.props.actionplanData.maxFilteredDate}
                                category={category}
                            />
                        </td>
                    </tr>
                );
                printedView = true;
                printedCategory = true;
            }
        }
        return rows;
    }

    render() {
        return (
            <table className={"timeline-table"}>
                <thead>
                    <tr>
                        <td className={"data"}>Status</td>
                        <td className={"data"}>Selected View</td>
                        <td className={"data display-id"}>Display Id</td>
                        <td className={"data comment-count"}>Comment Count</td>
                        <td>
                            <TimelineAxis
                                minDate={this.props.actionplanData.minFilteredDate}
                                maxDate={this.props.actionplanData.maxFilteredDate} />
                        </td>
                    </tr>
                </thead>
                <tbody>
                    { this.includeCategory(categories.OPEN_LATE) ? this.renderCategory(categories.OPEN_LATE) : null }
                    { this.includeCategory(categories.CLOSED_NOT_MET) ? this.renderCategory(categories.CLOSED_NOT_MET) : null }
                    { this.includeCategory(categories.OPEN_ON_TIME) ? this.renderCategory(categories.OPEN_ON_TIME) : null }
                    { this.includeCategory(categories.CLOSED_MET) ? this.renderCategory(categories.CLOSED_MET) : null}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={5}>
                            <p><span className={"open-late"}>&nbsp;&nbsp;&nbsp;&nbsp;</span> Open &amp; Late</p>
                            <p><span className={"open-on-time"}>&nbsp;&nbsp;&nbsp;&nbsp;</span> Open &amp; On Time</p>
                            <p><span className={"closed-not-met"}>&nbsp;&nbsp;&nbsp;&nbsp;</span> Closed &amp; Goal Not Met</p>
                            <p><span className={"closed-met"}>&nbsp;&nbsp;&nbsp;&nbsp;</span> Closed & Goal Met</p>
                            <p><span className={"intended"}>&nbsp;&nbsp;&nbsp;&nbsp;</span> Intended</p>
                            <p><span className={"comment"}>&nbsp;&nbsp;&nbsp;&nbsp;</span> Comment</p>
                        </td>
                    </tr>
                </tfoot>
            </table>
        );
    }
}
